// frontend/src/pages/AdminLeadsAnalytics.jsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkAdminAuth } from "../lib/adminAuth";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminLeadsAnalytics() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadLeads() {
      setLoading(true);
      setError("");

      try {
        const admin = await checkAdminAuth();
        if (!admin) {
          if (!cancelled) {
            navigate("/admin/login", { replace: true });
          }
          return;
        }

        const res = await fetch(
          `${API_BASE}/api/admin/leads?limit=200&skip=0&status=new`,
          { credentials: "include" },
        );

        if (!res.ok) {
          throw new Error("Leads request failed with status " + res.status);
        }

        const json = await res.json();
        if (cancelled) return;

        const items = Array.isArray(json.leads)
          ? json.leads
          : Array.isArray(json.items)
          ? json.items
          : [];

        setLeads(items);
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Failed to load admin leads.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadLeads();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  // --------- helpers to build simple analytics ---------

  const getLeadSource = (lead) =>
    (lead.source || lead.utm?.source || lead.utm_source || lead.channel || "UNKNOWN").toString();

  const getLeadService = (lead) =>
    (lead.serviceType || lead.service || lead.service_type || lead.category || "UNKNOWN").toString();

  const getLeadZip = (lead) =>
    (lead.zip || lead.zip_code || lead.contact?.zip || "-").toString();

  const formatLeadDate = (lead) => {
    const raw = lead.created_at || lead.created || lead.inserted_at || "";
    if (!raw) return "-";
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
  };

  const bySource = {};
  const byService = {};
  const byDay = {};

  for (const lead of leads) {
    const source = getLeadSource(lead);
    const service = getLeadService(lead);

    // YYYY-MM-DD from created_at
    const createdRaw =
      lead.created_at || lead.created || lead.inserted_at || "";
    let dayKey = "UNKNOWN";
    if (createdRaw) {
      try {
        const d = new Date(createdRaw);
        if (!isNaN(d.getTime())) {
          dayKey = d.toISOString().slice(0, 10); // 2025-12-04
        }
      } catch (_) {
        // ignore parse error, keep UNKNOWN
      }
    }

    bySource[source] = (bySource[source] || 0) + 1;
    byService[service] = (byService[service] || 0) + 1;
    byDay[dayKey] = (byDay[dayKey] || 0) + 1;
  }

  const totalLeads = leads.length;

  const topSourceCount =
    Object.values(bySource).reduce(
      (max, n) => (n > max ? n : max),
      0
    ) || 1;

  const topServiceCount =
    Object.values(byService).reduce(
      (max, n) => (n > max ? n : max),
      0
    ) || 1;

  const topDayCount =
    Object.values(byDay).reduce(
      (max, n) => (n > max ? n : max),
      0
    ) || 1;

  const sortedDays = Object.entries(byDay).sort(
    (a, b) => (a[0] || "").localeCompare(b[0] || "")
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl font-bold">Leads Analytics</h1>
            <p className="text-sm text-slate-400 mt-1">
              Simple owner dashboard powered by{" "}
              <code className="text-xs bg-slate-800 px-1 py-0.5 rounded">
                {API_BASE + "/api/admin/leads"}
              </code>
              .
            </p>
          </div>

        <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 mr-1" />
            Latest {totalLeads} leads (status: new)
          </div>
        </div>

        {loading && (
          <div className="text-slate-300">Loading analytics…</div>
        )}

        {error && (
          <div className="mb-4 rounded-lg border border-red-500/50 bg-red-950/40 px-4 py-3 text-sm text-red-200">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* Top-line metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <MetricCard
                label="Total leads in view"
                value={totalLeads || 0}
              />
              <MetricCard
                label="Distinct sources"
                value={Object.keys(bySource).length}
              />
              <MetricCard
                label="Distinct services"
                value={Object.keys(byService).length}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Leads by source */}
              <Panel title="Leads by source">
                <BarList data={bySource} max={topSourceCount} />
              </Panel>

              {/* Leads by service */}
              <Panel title="Leads by service">
                <BarList data={byService} max={topServiceCount} />
              </Panel>

              {/* Leads by day */}
              <Panel title="Leads by day (created_at)">
                {sortedDays.length === 0 ? (
                  <EmptyState />
                ) : (
                  <div className="space-y-1">
                    {sortedDays.map(([day, count]) => {
                      const pct = Math.round(
                        (count / topDayCount) * 100
                      );
                      return (
                        <div
                          key={day}
                          className="flex items-center gap-3 text-xs"
                        >
                          <div className="w-24 text-slate-300">
                            {day}
                          </div>
                          <div className="flex-1 bg-slate-800/60 rounded-full h-3 overflow-hidden">
                            <div
                              className="h-3 rounded-full bg-emerald-400/80"
                              style={{ width: `${pct || 4}%` }}
                            />
                          </div>
                          <div className="w-10 text-right text-slate-200">
                            {count}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Panel>

              {/* Raw count table (quick sanity view) */}
              <Panel title="Raw counts snapshot">
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                  <StatRow
                    label="Top source"
                    value={
                      Object.entries(bySource).sort(
                        (a, b) => b[1] - a[1]
                      )[0]?.[0] || "-"
                    }
                  />
                  <StatRow
                    label="Top service"
                    value={
                      Object.entries(byService).sort(
                        (a, b) => b[1] - a[1]
                      )[0]?.[0] || "-"
                    }
                  />
                  <StatRow
                    label="Most active day"
                    value={
                      Object.entries(byDay).sort(
                        (a, b) => b[1] - a[1]
                      )[0]?.[0] || "-"
                    }
                  />
                  <StatRow
                    label="Most leads in a day"
                    value={
                      Object.entries(byDay).sort(
                        (a, b) => b[1] - a[1]
                      )[0]?.[1] || 0
                    }
                  />
                </dl>
              </Panel>
            </div>

            <Panel title="Recent leads snapshot">
              {leads.length === 0 ? (
                <EmptyState />
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead className="text-slate-400 uppercase tracking-wide">
                      <tr className="border-b border-slate-800">
                        <th className="py-2 pr-4 text-left">Date</th>
                        <th className="py-2 pr-4 text-left">Source</th>
                        <th className="py-2 pr-4 text-left">Service</th>
                        <th className="py-2 pr-4 text-left">ZIP</th>
                        <th className="py-2 pr-4 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {leads.slice(0, 12).map((lead) => (
                        <tr key={lead.id || `${lead.created_at || "lead"}`} className="border-b border-slate-800/70">
                          <td className="py-2 pr-4 text-slate-300 whitespace-nowrap">
                            {formatLeadDate(lead)}
                          </td>
                          <td className="py-2 pr-4 text-slate-100">
                            {getLeadSource(lead)}
                          </td>
                          <td className="py-2 pr-4 text-slate-100">
                            {getLeadService(lead)}
                          </td>
                          <td className="py-2 pr-4 text-slate-300">
                            {getLeadZip(lead)}
                          </td>
                          <td className="py-2 pr-4 text-slate-300">
                            {lead.status || "new"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>
          </>
        )}

        {!loading && !error && leads.length === 0 && (
          <div className="mt-6 text-sm text-slate-400">
            No leads found for this filter. Try creating a few test
            leads from the public site and refreshing.
          </div>
        )}
      </div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 p-4">
      <div className="mb-3 text-sm font-medium text-slate-100">
        {title}
      </div>
      {children}
    </div>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
        {label}
      </div>
      <div className="text-xl font-semibold text-slate-50">
        {value}
      </div>
    </div>
  );
}

function BarList({ data, max }) {
  const entries = Object.entries(data || {}).sort(
    (a, b) => b[1] - a[1]
  );

  if (entries.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="space-y-1 text-xs">
      {entries.map(([key, count]) => {
        const pct = Math.round((count / max) * 100);
        return (
          <div
            key={key}
            className="flex items-center gap-3"
          >
            <div className="w-24 truncate text-slate-300">
              {key}
            </div>
            <div className="flex-1 bg-slate-800/60 rounded-full h-3 overflow-hidden">
              <div
                className="h-3 rounded-full bg-blue-400/80"
                style={{ width: `${pct || 4}%` }}
              />
            </div>
            <div className="w-10 text-right text-slate-200">
              {count}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <>
      <dt className="text-slate-400">{label}</dt>
      <dd className="text-slate-100">{value}</dd>
    </>
  );
}

function EmptyState() {
  return (
    <div className="text-xs text-slate-400">
      No data yet. Try generating a few test leads.
    </div>
  );
}
